// Unified central Quote-Peek modal — ONE component used across the Deal
// Pipeline, Lead Pipeline and Customer 360 timeline. Left: the real rendered
// quote preview (same HTML that feeds the PDF) with zoom + scroll. Right: a
// details panel (margin, urgency, delivery & site, status/tier/customer + AM)
// plus editable Staff Notes and Site/Delivery checkboxes and quick actions.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  X, ExternalLink, Download, Link2, Activity, FileText, Loader2,
  ZoomIn, ZoomOut, RotateCcw, Flame, Zap, Truck, MapPin, Send,
  TrendingUp, Crown, ShieldCheck, Check,
} from "lucide-react";
import ActivityLogModal from "./activity/ActivityLogModal";
import { resolveCrmPersonId } from "../lib/personResolve";
import OnSiteFacts from "../pages/quotes/panels/OnSiteFacts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const GBP = (n) =>
  n === null || n === undefined || !Number.isFinite(Number(n))
    ? "£0.00"
    : Number(n).toLocaleString("en-GB", { style: "currency", currency: "GBP" });

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "";
  const s = Math.round((Date.now() - +d) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const STATUS_TONE = {
  draft: "text-slate-300 bg-white/5 border-white/15",
  sent: "text-sky-300 bg-sky-500/10 border-sky-500/30",
  accepted: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  won: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  paid: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  rejected: "text-rose-300 bg-rose-500/10 border-rose-500/30",
  lost: "text-rose-300 bg-rose-500/10 border-rose-500/30",
  expired: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  archived: "text-slate-400 bg-white/5 border-white/10",
};

const TIER_STYLE = {
  BLACK: { cls: "bg-black text-white border-white/25", Icon: Crown },
  PLATINUM: { cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40", Icon: Crown },
  GOLD: { cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", Icon: Crown },
  SILVER: { cls: "bg-slate-400/15 text-slate-200 border-slate-400/40", Icon: ShieldCheck },
  BRONZE: { cls: "bg-orange-800/25 text-orange-300 border-orange-700/50", Icon: ShieldCheck },
};
function TierChip({ tier }) {
  if (!tier) return null;
  const key = String(tier).toUpperCase().split(/[\s-]/)[0];
  const s = TIER_STYLE[key] || { cls: "bg-white/5 text-slate-300 border-white/10", Icon: Crown };
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9.5px] font-mono uppercase tracking-widest border ${s.cls}`}>
      <Icon className="w-2.5 h-2.5" /> {tier}
    </span>
  );
}

// Blended / real margin — mirrors SummaryPanel.jsx exactly (incl. RWA45 branch
// deal + supplier rebate) so the peek matches the builder's figures.
function computeMargin(quote, subtotal) {
  const RWA45_SKUS = ["50300", "50302", "50303"];
  const RWA45_NAMES = ["galaxy - west london", "galaxy - east london"];
  const supplier = String(quote?.Supplier?.name || "").trim().toLowerCase();
  const branchActive = RWA45_NAMES.includes(supplier);
  const lines = quote?.LineItems || [];
  const qualifies = (it) => branchActive && RWA45_SKUS.some((n) => String(it.sku || "").includes(n));
  const totalCost = lines.reduce((s, it) => s + (Number(it.cost) || 0) * (Number(it.qty) || 0), 0);
  const branchDisc = lines.reduce(
    (s, it) => s + (qualifies(it) ? (Number(it.cost) || 0) * (Number(it.qty) || 0) * 0.05 : 0), 0);
  const adjustedCost = totalCost - branchDisc;
  const rebatePct = Number(quote?.RebatePct) || 0;
  const netCost = adjustedCost - adjustedCost * (rebatePct / 100);
  const realMarginPct = subtotal > 0 ? ((subtotal - netCost) / subtotal) * 100 : 0;
  return { totalCost, netCost, rebatePct, realMarginPct, realMargin: subtotal - netCost };
}
const marginTone = (pct) =>
  pct >= 15 ? "text-teal-300" : pct >= 10 ? "text-emerald-300" : pct >= 5 ? "text-amber-300" : "text-rose-300";
const marginWord = (pct) =>
  pct >= 15 ? "Great" : pct >= 10 ? "Healthy" : pct >= 5 ? "Lean" : "Very lean";

function urgencyTone(u) {
  if (u >= 9) return { cls: "bg-red-500/15 text-red-300 border-red-500/30", label: "Critical" };
  if (u >= 7) return { cls: "bg-orange-500/15 text-orange-300 border-orange-500/30", label: "Hot" };
  if (u >= 4) return { cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", label: "Warm" };
  return null;
}

// Editable on-site tick (local optimistic + PATCH Delivery).
function SiteTick({ label, checked, onToggle, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer py-0.5" data-testid={testid}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onToggle(e.target.checked)} className="accent-teal-500" />
      <span className="text-[12px] text-slate-200">{label}</span>
    </label>
  );
}

export default function QuotePeekModal({ quoteId, open, onClose }) {
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(0.75);
  const [frameH, setFrameH] = useState(900);
  const [noteText, setNoteText] = useState("");
  const [sendingNote, setSendingNote] = useState(false);
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [activityPerson, setActivityPerson] = useState(null); // {personId, am}
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!open || !quoteId) return;
    let alive = true;
    setLoading(true);
    setQuote(null);
    setHtml("");
    (async () => {
      try {
        const [{ data: q }, htmlRes] = await Promise.all([
          axios.get(`${API}/quotes/${quoteId}`, { withCredentials: true }),
          axios.get(`${API}/quotes/${quoteId}/pdf-preview`, {
            withCredentials: true, responseType: "text", transformResponse: [(v) => v],
          }),
        ]);
        if (!alive) return;
        setQuote(q);
        setHtml(typeof htmlRes.data === "string" ? htmlRes.data : String(htmlRes.data || ""));
      } catch {
        if (alive) toast.error("Couldn't load quote preview");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, quoteId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const measureFrame = useCallback(() => {
    try {
      const doc = iframeRef.current?.contentWindow?.document;
      if (doc?.body) setFrameH(Math.max(600, doc.body.scrollHeight + 24));
    } catch { /* cross-origin guard — srcDoc is same-origin so fine */ }
  }, []);

  const customer = quote?.Customer || {};
  const lines = quote?.LineItems || [];
  const delivery = quote?.Delivery || {};
  const staffNotes = quote?.StaffNotes || [];
  const subtotal = useMemo(
    () => lines.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unitPrice) || 0), 0),
    [lines],
  );
  const margin = useMemo(() => computeMargin(quote || {}, subtotal), [quote, subtotal]);
  const deliveryCost = Number(delivery.cost) || 0;
  const vat = (subtotal + deliveryCost) * 0.2;
  const total = subtotal + deliveryCost + vat;
  const statusKey = String(quote?.Status || "draft").toLowerCase();
  const tone = STATUS_TONE[statusKey] || STATUS_TONE.draft;
  const custName = [customer.firstName, customer.lastName].filter(Boolean).join(" ") || customer.company || customer.email || "No customer";
  const urgency = Number(quote?.Timeline?.urgency ?? quote?._urgency ?? 0);
  const asap = !!quote?.Timeline?.asap;
  const uTone = urgencyTone(urgency);

  const patchDelivery = async (patch) => {
    if (!quote?._id) return;
    const next = { ...delivery, ...patch };
    setQuote((q) => ({ ...q, Delivery: next }));
    setSavingDelivery(true);
    try {
      await axios.patch(`${API}/quotes/${quote._id}`, { Delivery: next }, { withCredentials: true });
    } catch {
      toast.error("Couldn't save delivery change");
      setQuote((q) => ({ ...q, Delivery: delivery })); // revert
    } finally {
      setSavingDelivery(false);
    }
  };

  const sendNote = async () => {
    const body = noteText.trim();
    if (!body || !quote?._id) return;
    setSendingNote(true);
    try {
      const { data } = await axios.post(`${API}/quotes/${quote._id}/notes`, { body }, { withCredentials: true });
      setQuote((q) => ({ ...q, StaffNotes: [...(q.StaffNotes || []), data.note] }));
      setNoteText("");
    } catch {
      toast.error("Couldn't add note");
    } finally {
      setSendingNote(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/quotes/${quote._id}/pdf?mode=download`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${quote.QuoteRef || quote._id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Download failed"); }
  };

  const copyLink = async () => {
    const link = `${window.location.origin}/quotes/${quote._id}/edit`;
    try { await navigator.clipboard.writeText(link); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy link"); }
  };

  const logActivity = async () => {
    const pid = await resolveCrmPersonId({ personId: customer.personId, email: customer.email });
    if (!pid) { toast.error("No linked customer record to log against"); return; }
    setActivityPerson({ personId: pid, am: quote.AccountManager });
  };

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[160] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6"
        onClick={onClose}
        data-testid="quote-peek-modal-overlay"
      >
        <div
          className="w-full max-w-[1200px] h-[92vh] rounded-2xl overflow-hidden flex flex-col bg-[#0B1120] border border-white/12 shadow-[0_30px_90px_rgba(0,0,0,0.7)]"
          onClick={(e) => e.stopPropagation()}
          data-testid="quote-peek-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-white/10 bg-[#0B1120]/95 shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <FileText className="w-4 h-4 text-orange-400 shrink-0" />
              <div className="min-w-0">
                <div className="font-mono text-sm font-bold text-white truncate" data-testid="quote-peek-ref">
                  {quote?.QuoteRef || "Quote"}
                </div>
                <div className="text-[11px] text-slate-500 truncate">{custName}</div>
              </div>
              {quote && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider border ${tone}`} data-testid="quote-peek-status">
                  {quote.Status || "Draft"}
                </span>
              )}
              <TierChip tier={customer.shortTier || customer.tier} />
            </div>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10 text-slate-400" data-testid="quote-peek-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading quote…
            </div>
          ) : !quote ? (
            <div className="flex-1 flex items-center justify-center text-slate-500">Quote not found.</div>
          ) : (
            <div className="flex-1 flex min-h-0 flex-col lg:flex-row">
              {/* ── Left: rendered preview with zoom + scroll ── */}
              <div className="lg:w-[58%] flex flex-col min-h-0 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0A0F1A]">
                <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 shrink-0">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mr-auto">Preview</span>
                  <button onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))} className="w-7 h-7 grid place-items-center rounded border border-white/10 text-slate-300 hover:bg-white/5" data-testid="quote-peek-zoom-out" title="Zoom out"><ZoomOut className="w-3.5 h-3.5" /></button>
                  <span className="text-[11px] font-mono text-slate-400 w-10 text-center tabular-nums" data-testid="quote-peek-zoom-level">{Math.round(zoom * 100)}%</span>
                  <button onClick={() => setZoom((z) => Math.min(1.5, +(z + 0.1).toFixed(2)))} className="w-7 h-7 grid place-items-center rounded border border-white/10 text-slate-300 hover:bg-white/5" data-testid="quote-peek-zoom-in" title="Zoom in"><ZoomIn className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setZoom(0.75)} className="w-7 h-7 grid place-items-center rounded border border-white/10 text-slate-300 hover:bg-white/5" data-testid="quote-peek-zoom-reset" title="Reset zoom"><RotateCcw className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex-1 overflow-auto p-3" data-testid="quote-peek-preview-scroll">
                  <div style={{ width: `${100 / zoom}%`, transform: `scale(${zoom})`, transformOrigin: "top left" }}>
                    <iframe
                      ref={iframeRef}
                      srcDoc={html}
                      onLoad={measureFrame}
                      title="Quote preview"
                      style={{ width: "100%", height: frameH, border: 0, background: "#fff", borderRadius: 6 }}
                      data-testid="quote-peek-iframe"
                    />
                  </div>
                </div>
              </div>

              {/* ── Right: details + editable + actions ── */}
              <div className="lg:w-[42%] flex flex-col min-h-0">
                <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="quote-peek-panel">
                  {/* Margin + totals */}
                  <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3.5" data-testid="quote-peek-margin">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Margin</span>
                      <span className={`text-[11px] font-mono ${marginTone(margin.realMarginPct)}`}>{marginWord(margin.realMarginPct)}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-3xl font-bold tabular-nums ${marginTone(margin.realMarginPct)}`} data-testid="quote-peek-margin-pct">{margin.realMarginPct.toFixed(1)}%</span>
                      <span className="text-[11px] text-slate-500">{margin.rebatePct > 0 ? "real (post-rebate)" : "blended"}</span>
                    </div>
                    <div className="mt-2.5 pt-2.5 border-t border-white/10 space-y-1 text-[12.5px]">
                      <div className="flex justify-between text-slate-400"><span>Products (ex VAT)</span><span className="font-mono">{GBP(subtotal)}</span></div>
                      <div className="flex justify-between text-slate-400"><span>Delivery</span><span className="font-mono">{deliveryCost > 0 ? GBP(deliveryCost) : "FREE"}</span></div>
                      <div className="flex justify-between text-slate-400"><span>VAT 20%</span><span className="font-mono">{GBP(vat)}</span></div>
                      <div className="flex justify-between text-white font-bold pt-1 border-t border-white/10"><span>Total inc VAT</span><span className="font-mono text-orange-400">{GBP(total)}</span></div>
                    </div>
                  </div>

                  {/* Signals: urgency / ASAP / build */}
                  {(uTone || asap || quote.BuildType) && (
                    <div className="flex items-center gap-1.5 flex-wrap" data-testid="quote-peek-signals">
                      {uTone && <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${uTone.cls}`}><Flame className="w-2.5 h-2.5" />{uTone.label} {urgency}</span>}
                      {asap && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border bg-rose-500/15 text-rose-200 border-rose-500/30"><Zap className="w-2.5 h-2.5" />ASAP</span>}
                      {quote.BuildType && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] border bg-white/5 text-slate-300 border-white/10">{quote.BuildType}</span>}
                    </div>
                  )}

                  {/* Customer + AM */}
                  <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3.5" data-testid="quote-peek-customer">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Customer</div>
                    <div className="text-white font-semibold">{custName}</div>
                    {customer.company && <div className="text-sm text-slate-400">{customer.company}</div>}
                    {customer.email && <div className="text-xs text-slate-500 font-mono mt-0.5">{customer.email}</div>}
                    {customer.phone && <div className="text-xs text-slate-500 font-mono">{customer.phone}</div>}
                    <div className="mt-2 pt-2 border-t border-white/10 flex items-center justify-between text-[11px]">
                      <span className="font-mono uppercase tracking-widest text-slate-500">Account manager</span>
                      <span className="text-slate-200">{quote.AccountManager || "—"}</span>
                    </div>
                  </div>

                  {/* Delivery & Site — editable checkboxes */}
                  <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3.5 space-y-2" data-testid="quote-peek-delivery">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5"><Truck className="w-3 h-3" /> Delivery &amp; site</span>
                      {savingDelivery && <Loader2 className="w-3 h-3 animate-spin text-teal-300" />}
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11.5px]">
                      <div className="text-slate-500">Method</div><div className="text-slate-200 text-right">{delivery.method || "Our"}</div>
                      <div className="text-slate-500">Band</div><div className="text-slate-200 text-right truncate">{delivery.band || "—"}</div>
                      {delivery.deliveryWindow && (<><div className="text-slate-500">Window</div><div className="text-slate-200 text-right truncate">{delivery.deliveryWindow}</div></>)}
                      {delivery.estimatedWeightKg ? (<><div className="text-slate-500">Est. weight</div><div className="text-slate-200 text-right">{delivery.estimatedWeightKg} kg</div></>) : null}
                    </div>
                    {(delivery.siteContactName || delivery.access || delivery.parking) && (
                      <div className="pt-1.5 border-t border-white/10 text-[11.5px] space-y-0.5">
                        {delivery.siteContactName && <div className="flex items-center gap-1.5 text-slate-300"><MapPin className="w-3 h-3 text-slate-500" />{delivery.siteContactName}{delivery.siteContactPhone ? ` · ${delivery.siteContactPhone}` : ""}</div>}
                        {delivery.access && <div className="text-slate-400">Access: {delivery.access}</div>}
                        {delivery.parking && <div className="text-slate-400">Parking: {delivery.parking}</div>}
                      </div>
                    )}
                    <div className="pt-1.5 border-t border-white/10">
                      <div className="space-y-1.5 mb-1.5"><OnSiteFacts delivery={delivery} onPatch={patchDelivery} testidPrefix="quote-peek-onsite" /></div>
                      <SiteTick label="✓ Confirmed all of the above with customer" checked={delivery.onSiteChecked} onToggle={(v) => patchDelivery({ onSiteChecked: v })} testid="quote-peek-tick-confirmed" />
                    </div>
                  </div>

                  {/* Staff notes — editable */}
                  <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3.5" data-testid="quote-peek-staff-notes">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-brass mb-2" style={{ color: "#C8A46B" }}>CW staff notes · {staffNotes.length}</div>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                      {staffNotes.length === 0 && <div className="text-[11.5px] text-slate-500 text-center py-2">No notes yet.</div>}
                      {staffNotes.map((n) => (
                        <div key={n.id} className="rounded-md bg-white/[0.03] border border-white/[0.06] px-2.5 py-1.5" data-testid={`quote-peek-note-${n.id}`}>
                          <div className="text-[12.5px] text-slate-200 whitespace-pre-wrap break-words">{n.body}</div>
                          <div className="text-[10px] text-slate-500 font-mono mt-0.5">{n.by} · {timeAgo(n.ts)}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-1.5 mt-2 pt-2 border-t border-white/10">
                      <input
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendNote(); } }}
                        placeholder="Add a staff note…"
                        data-testid="quote-peek-note-input"
                        className="flex-1 px-2.5 py-1.5 rounded-md bg-white/[0.04] border border-white/10 text-[12.5px] text-white placeholder:text-slate-500 outline-none focus:border-orange-400/60"
                      />
                      <button onClick={sendNote} disabled={sendingNote || !noteText.trim()} data-testid="quote-peek-note-send"
                        className="px-2.5 rounded-md bg-orange-500/90 hover:bg-orange-400 text-black disabled:opacity-40">
                        {sendingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Actions footer */}
                <div className="shrink-0 border-t border-white/10 bg-white/[0.02] p-3 grid grid-cols-2 gap-2">
                  <button onClick={() => navigate(`/quotes/${quote._id}/edit`)} data-testid="quote-peek-open-full"
                    className="col-span-2 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 text-black text-sm font-semibold">
                    <ExternalLink className="w-4 h-4" /> Open full builder
                  </button>
                  <button onClick={downloadPdf} data-testid="quote-peek-download"
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/12 text-slate-200 text-[12.5px] hover:bg-white/5">
                    <Download className="w-3.5 h-3.5" /> Download PDF
                  </button>
                  <button onClick={copyLink} data-testid="quote-peek-copy-link"
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/12 text-slate-200 text-[12.5px] hover:bg-white/5">
                    <Link2 className="w-3.5 h-3.5" /> Copy link
                  </button>
                  <button onClick={logActivity} data-testid="quote-peek-log-activity"
                    className="col-span-2 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-purple-500/40 bg-purple-500/10 text-purple-200 text-[12.5px] hover:bg-purple-500/20">
                    <Activity className="w-3.5 h-3.5" /> Log activity / call
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {activityPerson && (
        <ActivityLogModal
          personId={activityPerson.personId}
          defaultAssigneeName={activityPerson.am}
          contextLabel={quote?.QuoteRef}
          onClose={() => setActivityPerson(null)}
          onSaved={() => setActivityPerson(null)}
        />
      )}
    </>,
    document.body,
  );
}
